import { type MutableRefObject, type ReactElement, useRef } from 'react';
import {
  NeonSelectField,
  NeonCheckboxSingle,
  NeonButton,
  NeonCardStandard,
  NeonMultiSelectField,
  NeonChipMultiSelectField,
} from '@ps-refarch-ux/neon';
import '@/styles/filter-panel.css';
import {
  filterPanelContainerStyle,
  filterBodyStyle,
  filterScrollAreaStyle,
  filterFooterStyle,
  filterRangeRowStyle,
  filterRangeSelectStyle,
  filterRangeToLabelStyle,
  selectedSectionStyle,
  selectedLabelStyle,
  chipsWrapStyle,
  chipStyle,
  chipTextStyle,
  chipRemoveStyle,
  clearAllRowStyle,
  clearAllStyle,
  selectedDividerStyle,
} from './filter-panel.styles';
import { isDistrictStaffForPos } from '@/utils/cp-context';

export type FilterFieldSelect = {
  type: 'select';
  id: string;
  label: string;
  value: string;
  options: Array<{ text: string; value: string }>;
  showLabel?: boolean;
  undefinedOption?: boolean;
  /** Optional sublabel / instruction (Neon `dataHelperText`). */
  helperText?: string;
};

export type FilterFieldMultiSelect = {
  helperText?: string | undefined;
  type: 'multiselect';
  id: string;
  label: string;
  value: string[];
  placeholder?: string;
  options: Array<{ id: string; name: string; labelText: string }>;
  disabled?: boolean;
};

export type FilterFieldCheckbox = {
  type: 'checkbox';
  id: string;
  label: string;
  checked: boolean;
  /** Optional helper (Neon `dataHelperText`), e.g. under the checkbox. */
  helperText?: string;
};

export type FilterFieldRange = {
  type: 'range';
  fromField: Omit<FilterFieldSelect, 'type'>;
  toField: Omit<FilterFieldSelect, 'type'>;
  toLabel?: string;
};

/** Grade-style chip/toggle grid (e.g. 6–12); uses NeonChipMultiSelectField for same structure and sizes as design. */
export type FilterFieldChipMultiselect = {
  type: 'chipMultiselect';
  id: string;
  label: string;
  value: string[];
  options: Array<{ id: string; name: string; labelText: string }>;
};

export type FilterField =
  | FilterFieldSelect
  | FilterFieldMultiSelect
  | FilterFieldChipMultiselect
  | FilterFieldCheckbox
  | FilterFieldRange;

export interface FilterChip {
  id: string;
  label: string;
  removable?: boolean;
}

export interface FilterPanelProps {
  title: string;
  fields: FilterField[];
  chips?: FilterChip[];
  onFieldChange: (fieldId: string, value: unknown) => void;
  onChipRemove?: (chipId: string) => void;
  onClearAllChips?: () => void;
  onClose: () => void;
  onApply?: () => void;
  applyText?: string;
  closeButtonId?: string;
  applyButtonId?: string;
  hasChanges?: boolean;
  hideCloseButton?: boolean;
}

export const FilterPanel = ({
  title,
  fields,
  chips = [],
  onFieldChange,
  onChipRemove,
  onClearAllChips,
  onClose,
  onApply,
  applyText = 'Apply',
  closeButtonId = 'filter-close-button',
  applyButtonId = 'filter-apply-button',
  hasChanges,
  hideCloseButton = false,
}: FilterPanelProps): ReactElement => {
  // When one multiselect updates, other Neon fields can fire with []; ignore those spurious clears
  const lastUpdatedFieldIdRef = useRef<string | null>(null);

  return (
    <div style={filterPanelContainerStyle} data-testid="filter-panel">
      <NeonCardStandard dataFullHeight="true" dataHeading={title} dataInjectionSpacing="false">
        {!hideCloseButton && (
          <div data-slot="card-actions">
            <NeonButton
              id={closeButtonId}
              dataType="utility"
              dataText="Close filters"
              dataIcon="close"
              onClick={onClose}
            />
          </div>
        )}
        <div data-slot="body" style={filterBodyStyle}>
          <div style={filterScrollAreaStyle}>
            {chips.length > 0 && (
              <>
                <div style={selectedSectionStyle}>
                  <span style={selectedLabelStyle}>Selected</span>
                  <div style={chipsWrapStyle}>
                    {chips.map((chip) => (
                      <span
                        key={chip.id}
                        style={chipStyle}
                        onClick={(e): void => e.stopPropagation()}
                        onKeyDown={(e): void => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                          }
                        }}
                      >
                        <span style={chipTextStyle}>{chip.label}</span>
                        {onChipRemove && chip.removable !== false && (
                          <button
                            type="button"
                            style={chipRemoveStyle}
                            aria-label={`Remove ${chip.label}`}
                            onClick={(e): void => {
                              e.preventDefault();
                              e.stopPropagation();
                              onChipRemove(chip.id);
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {onClearAllChips && (
                    <div style={clearAllRowStyle}>
                      <button
                        type="button"
                        style={clearAllStyle}
                        onClick={(e): void => {
                          e.preventDefault();
                          e.stopPropagation();
                          onClearAllChips();
                        }}
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
                <hr style={selectedDividerStyle} />
              </>
            )}
            {fields.map((field) => renderField(field, onFieldChange, lastUpdatedFieldIdRef))}
          </div>
          {onApply && (
            <div style={filterFooterStyle}>
              <NeonButton
                id={applyButtonId}
                dataType="primary"
                dataText={applyText}
                disabled={!hasChanges}
                onClick={onApply}
              />
            </div>
          )}
        </div>
      </NeonCardStandard>
    </div>
  );
};

function stringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((x, i) => x === b[i]);
}

function renderField(
  field: FilterField,
  onFieldChange: (fieldId: string, value: unknown) => void,
  lastUpdatedFieldIdRef: MutableRefObject<string | null>
): ReactElement {
  switch (field.type) {
    case 'select':
      return (
        <NeonSelectField
          key={field.id}
          id={field.id}
          dataLabelText={field.label}
          dataShowLabel={field.showLabel}
          dataUndefinedOption={field.undefinedOption ? 'true' : undefined}
          {...(field.helperText ? { dataHelperText: field.helperText } : {})}
          modelValue={field.value}
          modelValueChange={(newValue: string | undefined): void => {
            onFieldChange(field.id, newValue ?? '');
          }}
          options={field.options}
        />
      );
    case 'multiselect':
      return (
        <div key={field.id} className="filter-multiselect-wrapper">
          <NeonMultiSelectField
            id={field.id}
            dataLabelText={field.label}
            dataDropDownText={field.placeholder ?? 'Select'}
            options={field.options}
            modelValue={field.value}
            disabled={field.disabled}
            {...(field.helperText && isDistrictStaffForPos()
              ? { dataHelperText: field.helperText }
              : {})}
            modelValueChange={(v: string[] | undefined): void => {
              if (v === undefined || !Array.isArray(v)) {
                return;
              }
              const current = field.value;
              // Ignore spurious empty updates from other Neon fields re-rendering (CC has multiple multiselects)
              if (
                v.length === 0 &&
                Array.isArray(current) &&
                current.length > 0 &&
                field.id !== lastUpdatedFieldIdRef.current
              ) {
                return;
              }
              if (!Array.isArray(current) || !stringArraysEqual(current, v)) {
                lastUpdatedFieldIdRef.current = field.id;
                onFieldChange(field.id, v);
              }
            }}
            dataShowFilter
            dataShowSelectedItemsList={false}
          />
        </div>
      );
    case 'chipMultiselect':
      return (
        <div key={field.id} className="filter-multiselect-wrapper">
          <NeonChipMultiSelectField
            id={field.id}
            dataLabelText={field.label}
            options={field.options}
            modelValue={field.value}
            modelValueChange={(v: string[] | undefined): void => {
              if (v === undefined || !Array.isArray(v)) {
                return;
              }
              const current = field.value;
              // Ignore spurious empty updates from other Neon fields re-rendering (CC has multiple multiselects)
              if (
                v.length === 0 &&
                Array.isArray(current) &&
                current.length > 0 &&
                field.id !== lastUpdatedFieldIdRef.current
              ) {
                return;
              }
              if (!Array.isArray(current) || !stringArraysEqual(current, v)) {
                lastUpdatedFieldIdRef.current = field.id;
                onFieldChange(field.id, v);
              }
            }}
          />
        </div>
      );
    case 'checkbox':
      return (
        <NeonCheckboxSingle
          key={field.id}
          id={field.id}
          dataLabelText={field.label}
          {...(field.helperText ? { dataHelperText: field.helperText } : {})}
          value={field.checked ? 'true' : 'false'}
          onInput={(): void => {
            onFieldChange(field.id, !field.checked);
          }}
        />
      );
    case 'range':
      return (
        <div key={`${field.fromField.id}-range`} style={filterRangeRowStyle}>
          <div style={filterRangeSelectStyle}>
            <NeonSelectField
              id={field.fromField.id}
              dataLabelText={field.fromField.label}
              dataUndefinedOption={field.fromField.undefinedOption ? 'true' : undefined}
              modelValue={field.fromField.value}
              modelValueChange={(newValue: string | undefined): void => {
                onFieldChange(field.fromField.id, newValue ?? '');
              }}
              options={field.fromField.options}
            />
          </div>
          <span style={filterRangeToLabelStyle}>{field.toLabel ?? 'to'}</span>
          <div style={filterRangeSelectStyle}>
            <NeonSelectField
              id={field.toField.id}
              dataLabelText={field.toField.label || field.fromField.label || 'Select'}
              dataShowLabel={field.toField.showLabel}
              dataUndefinedOption={field.toField.undefinedOption ? 'true' : undefined}
              modelValue={field.toField.value}
              modelValueChange={(newValue: string | undefined): void => {
                onFieldChange(field.toField.id, newValue ?? '');
              }}
              options={field.toField.options}
            />
          </div>
        </div>
      );
  }
}
